import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Contact from '../components/Contact';
import { casesById } from '../data/cases';

const CaseDetail = () => {
  const { caseId, lang = 'en' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('services');

  const allCases = casesById;

  const caseStudy = allCases[caseId];

  if (!caseStudy) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Case Not Found</h1>
          <button
            onClick={() => navigate(`/${lang}/cases`)}
            className="px-6 py-3 bg-white text-black rounded-lg font-semibold hover:bg-white/90 transition-all"
          >
            {t('caseDetail.backToAllCases')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen">
      {/* Header Section with Image */}
      <section className="relative bg-gradient-to-br from-[#000000] via-[#000000] to-[#0a0a0a] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Back Button */}
              <button
                onClick={() => navigate(`/${lang}/cases`)}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors group mb-4"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('caseDetail.backToAllCases')}
              </button>

              {/* Badge */}
              <p className="text-white/60 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                {caseStudy.category}
              </p>

              {/* Title */}
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                {caseStudy.title}
              </h1>

              {/* Mobile Image - Visible only on mobile, between Title and Tags */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="block lg:hidden relative my-8"
              >
                <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 aspect-[4/3] shadow-2xl border border-white/10">
                  <img
                    src={caseStudy.screenshot}
                    alt={`${caseStudy.title} screenshot`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>

              {/* Tags */}
              <div className="flex gap-3 flex-wrap">
                {caseStudy.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-bold rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p className="text-white/70 text-lg leading-relaxed">
                {caseStudy.description}
              </p>

              {/* Visit Website Button */}
              <div className="pt-4">
                <a
                  href={caseStudy.visitLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 pl-7 pr-1 py-1 bg-white text-black rounded-full font-bold text-sm hover:bg-white/90 transition-all duration-300 group"
                >
                  {t('caseDetail.visitWebsite')}
                  <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center transition-transform group-hover:scale-110">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </a>
              </div>

              {/* Legal Links */}
              {(caseStudy.privacyPolicyLink || caseStudy.termsOfServiceLink) && (
                <div className="flex gap-4 pt-2">
                  {caseStudy.privacyPolicyLink && (
                    <a
                      href={caseStudy.privacyPolicyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/40 text-sm hover:text-white transition-colors border-b border-transparent hover:border-white"
                    >
                      Privacy Policy
                    </a>
                  )}
                  {caseStudy.termsOfServiceLink && (
                    <a
                      href={caseStudy.termsOfServiceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/40 text-sm hover:text-white transition-colors border-b border-transparent hover:border-white"
                    >
                      Terms of Service
                    </a>
                  )}
                </div>
              )}
            </motion.div>

            {/* Right - Image (Desktop Only) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 aspect-[4/3]">
                <img
                  src={caseStudy.screenshot}
                  alt={`${caseStudy.title} screenshot`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        <div class="text-center p-8">
                          <div class="text-6xl mb-4">📱</div>
                          <p class="text-white font-bold text-xl">${caseStudy.title}</p>
                          <p class="text-gray-400 text-sm mt-2">${caseStudy.category}</p>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
            </motion.div>
          </div>

          {/* Project Info Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-white/10"
          >
            <div>
              <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">{t('caseDetail.client')}</p>
              <p className="text-white text-base font-medium">{caseStudy.client}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">{t('caseDetail.industry')}</p>
              <p className="text-white text-base font-medium">{caseStudy.industry}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">{t('caseDetail.location')}</p>
              <p className="text-white text-base font-medium">{caseStudy.location}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">{t('caseDetail.year')}</p>
              <p className="text-white text-base font-medium">{caseStudy.year}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="bg-black py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t('caseDetail.keyMetrics')}</h2>
            <p className="text-white/60 text-lg">{t('caseDetail.metricsDescription')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudy.metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#0a0a0a] rounded-2xl p-8 text-center"
              >
                <p className="text-5xl lg:text-6xl font-bold text-white mb-3">{metric.value}</p>
                <p className="text-white/60 text-sm font-semibold uppercase tracking-wider">{metric.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Overview Section */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Column */}
            <div className="space-y-12">
              {/* Challenge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h3 className="text-2xl font-bold text-white mb-4">{t('caseDetail.challenge')}</h3>
                <p className="text-white/70 text-lg leading-relaxed">{caseStudy.challenge}</p>
              </motion.div>

              {/* Solution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h3 className="text-2xl font-bold text-white mb-4">{t('caseDetail.solution')}</h3>
                <p className="text-white/70 text-lg leading-relaxed">{caseStudy.solution}</p>
              </motion.div>
            </div>

            {/* Right Column */}
            <div className="space-y-12">
              {/* Results */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="text-2xl font-bold text-white mb-4">{t('caseDetail.keyMetrics')}</h3>
                <p className="text-white/70 text-lg leading-relaxed">{caseStudy.results}</p>
              </motion.div>

              {/* Technologies */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h3 className="text-2xl font-bold text-white mb-4">{t('caseDetail.techStack')}</h3>
                <div className="flex gap-3 flex-wrap">
                  {caseStudy.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <Contact />
    </div>
  );
};

export default CaseDetail;
